import React, {
    useCallback, useEffect, useLayoutEffect, useRef, useState,
} from 'react';
import CriteriaTagStrip from './CriteriaTagStrip';
import { BUILD_NOTE_MAX_LEN } from '../../core/GenConfig';
import './BuildInfoPanel.css';

const TXT_EN_US = { fr: 'fr-FR', en: 'en-US', de: 'de-DE', es: 'es-ES', pt: 'pt-BR' };

const TXT_CREATED = { fr: 'Créé', en: 'Created', de: 'Erstellt', es: 'Creado', pt: 'Criado' };
const TXT_UPDATED = { fr: 'Modifié', en: 'Updated', de: 'Geändert', es: 'Modificado', pt: 'Modificado' };
const TXT_TAGS = { fr: 'Tags', en: 'Tags', de: 'Tags', es: 'Tags', pt: 'Tags' };
const TXT_NOTE = { fr: 'Note', en: 'Note', de: 'Notiz', es: 'Nota', pt: 'Nota' };

function formatDate(value, lang) {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    return new Intl.DateTimeFormat(TXT_EN_US[lang] || TXT_EN_US.en, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    }).format(date);
}

function fitTextareaHeight(el) {
    if (!el) return;
    const cs = getComputedStyle(el);
    const minPx = parseFloat(cs.minHeight) || 0;
    const maxPx = parseFloat(cs.maxHeight);
    const cap = Number.isFinite(maxPx) && maxPx > 0 ? maxPx : 12 * 16;
    el.style.height = 'auto';
    const sh = el.scrollHeight;
    const next = Math.min(cap, Math.max(minPx, sh));
    el.style.height = `${next}px`;
    el.style.overflowY = sh > cap ? 'auto' : 'hidden';
}

const NOTE_FLUSH_MS = 400;

const BuildInfoPanel = ({
    createdAt,
    updatedAt,
    criteriaTags = [],
    className = '',
    lang = 'fr',
    buildNote = '',
    onBuildNoteChange,
    noteResetKey = '',
    readOnly = false,
}) => {
    const hasCriteriaTags = Array.isArray(criteriaTags) && criteriaTags.length > 0;
    const hasDates = !!(createdAt || updatedAt);
    const [draft, setDraft] = useState(() => buildNote ?? '');
    const noteRef = useRef(null);
    const flushTimerRef = useRef(null);
    const draftRef = useRef(draft);
    const onNoteCommitRef = useRef(onBuildNoteChange);
    const readOnlyRef = useRef(readOnly);

    draftRef.current = draft;
    onNoteCommitRef.current = onBuildNoteChange;
    readOnlyRef.current = readOnly;

    useEffect(() => {
        setDraft(buildNote ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-sync when switching builds; do not reset on parent buildNote updates (avoids clobbering ahead-of-save typing).
    }, [noteResetKey]);

    useEffect(() => () => {
        if (flushTimerRef.current) {
            clearTimeout(flushTimerRef.current);
            flushTimerRef.current = null;
        }
        const cb = onNoteCommitRef.current;
        if (cb && !readOnlyRef.current) {
            cb(draftRef.current);
        }
    }, []);

    const flushNote = useCallback((value) => {
        if (readOnly || !onNoteCommitRef.current) return;
        onNoteCommitRef.current(value);
    }, [readOnly]);

    const scheduleNoteFlush = useCallback((value) => {
        if (flushTimerRef.current) {
            clearTimeout(flushTimerRef.current);
        }
        flushTimerRef.current = setTimeout(() => {
            flushTimerRef.current = null;
            flushNote(value);
        }, NOTE_FLUSH_MS);
    }, [flushNote]);

    const syncNoteHeight = useCallback(() => {
        fitTextareaHeight(noteRef.current);
    }, []);

    useLayoutEffect(() => {
        syncNoteHeight();
    }, [syncNoteHeight, hasDates, hasCriteriaTags, draft]);

    const handleNoteChange = (e) => {
        const v = e.target.value;
        setDraft(v);
        if (!readOnly) {
            scheduleNoteFlush(v);
        }
    };

    const handleNoteBlur = () => {
        if (flushTimerRef.current) {
            clearTimeout(flushTimerRef.current);
            flushTimerRef.current = null;
        }
        if (!readOnly) {
            flushNote(draft);
        }
    };

    return (
        <aside className={`build-info-panel ${className}`.trim()}>
            {hasCriteriaTags ? (
                <div className="build-info-tags">
                    <span className="build-info-label">{TXT_TAGS[lang] || TXT_TAGS.en}</span>
                    <CriteriaTagStrip tags={criteriaTags} lang={lang} className="build-info-tag-strip" maxIcons={12} />
                </div>
            ) : null}
            {hasDates ? (
                <>
                    <div className="build-info-meta">
                        <span className="build-info-label">{TXT_CREATED[lang] || TXT_CREATED.en}</span>
                        <span className="build-info-value">{formatDate(createdAt, lang)}</span>
                    </div>
                    <div className="build-info-meta">
                        <span className="build-info-label">{TXT_UPDATED[lang] || TXT_UPDATED.en}</span>
                        <span className="build-info-value">{formatDate(updatedAt, lang)}</span>
                    </div>
                </>
            ) : null}
            <div className="build-info-note">
                <span className="build-info-label">{TXT_NOTE[lang] || TXT_NOTE.en}</span>
                <textarea
                    ref={noteRef}
                    className="build-info-note-input"
                    placeholder="..."
                    rows={1}
                    value={draft}
                    maxLength={BUILD_NOTE_MAX_LEN}
                    readOnly={readOnly}
                    onChange={handleNoteChange}
                    onBlur={handleNoteBlur}
                    onInput={syncNoteHeight}
                />
            </div>
        </aside>
    );
};

export default BuildInfoPanel;
